<template>
  <div class="tpl-header">
    <transition name="fade" :duration="{ enter: 0, leave: 500 }">
      <gd-header
        v-if="header"
        :theme="theme"
        :header-blank="!headerBlankRef"
        :partner="storageGd.partner"
        :black-ribbon="blackRibbon"
      >
        <template v-slot:accountbox>
          <accountbox
            @onShowProfile="accountboxNavigationHandler"
            @onShowFriends="accountboxNavigationHandler"
            @onShowMessages="accountboxNavigationHandler"
            @onShowChallanges="accountboxNavigationHandler"
            @onPayment="accountboxNavigationPaymentHandler"
            @onLoyality="accountboxNavigationHandler"
            :force-load-data="false"
            class="accountbox-wrap base dsari"
          ></accountbox>
        </template>
        <template v-slot:navigation-tabs>
          <gd-navigation-tabs :loyalty="storageUser.loyalty" :tab="navTab" @link="onLink" />
        </template>
      </gd-header>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { GD, STORE_NAME, Storage, User } from '@gd/storage'
import Accountbox from '@gd/accountbox'
import { GdNavigationTabs, GdHeader } from '@gd/classic-template'
import events from '@gd/event-bus'

/**
 * * theme: winter
 * * selectedTab: home, games, myGameduell, community, infoAndHelp
 * * headerBlank: initializes the header with blank style ( like for the games ) or standard
 * * blackRibbon: activate the black ribbon
 */
const props = withDefaults(
  defineProps<{ selectedTab: string; theme: string; headerBlank: boolean; blackRibbon: boolean }>(),
  { selectedTab: '', theme: '', headerBlank: false, blackRibbon: false }
)

const headerBlankRef = ref(props.headerBlank)
const selectedTabRef = ref(props.selectedTab)
const header = ref(true)
const storageGd: GD = new Storage().read(STORE_NAME.GD)
const storageUser: User = new Storage().read(STORE_NAME.USER)

onMounted(() => {
  events.$on('template', (obj: { headerBlank: boolean; footerBlank: boolean; header: boolean; footer: boolean }) => {
    if (Object.prototype.hasOwnProperty.call(obj, 'headerBlank')) {
      headerBlankRef.value = obj.headerBlank
    }

    if (Object.prototype.hasOwnProperty.call(obj, 'header')) {
      header.value = obj.header
    }
  })
})

function onLink(tabName, event) {
  navTab.value = tabName
  // prevent bubbling to avoid href execution (href on link is only for mouse middle click to open in new tab)
  event.preventDefault()
}

const navTab = computed({
  get() {
    return selectedTabRef.value
  },
  set(name: string) {
    selectedTabRef.value = name
    let url = ''
    switch (name) {
      case 'home':
        url = '/gd/homepage/home.xhtml'
        break
      case 'games':
        url = '/gd/gamesPage/gamespage.xhtml'
        break
      case 'community':
        url = '/gd/playerProfile/showProfile.xhtml'
        break
      case 'myGameduell':
        url = '/gd/myGameDuell/playerInfo.xhtml'
        break
      case 'infoAndHelp':
        url = '/gd/help/help.xhtml'
        break
    }
    location.href = url || '#'
  },
})

function accountboxNavigationHandler(nav: { url: string; button: number; type: string }) {
  // LOPS-6861 disable tracking
  if (nav.button === 1) {
    window.open(nav.url, undefined, 'noopener')
  } else {
    location.href = nav.url
  }
}

function accountboxNavigationPaymentHandler() {
  // LOPS-6861 disable tracking
  events.$emit('deposit', { trackingId: 'accountbox' })
}
</script>

<style lang="less">
@import '~@npm/@gd/classic-template/lib/header';
@import '~@npm/@gd/classic-template/lib/navigationTabs';
#navigation_top {
  // set initial min height ( avoid empty header flickering when page loads )
  // data attributes will be removed when vue has been initializes
  &[data-tab] {
    min-height: 125px;
  }
  // blank header need smaler min-height
  &[data-header-blank='true'] {
    min-height: 82px;
  }
}
.tpl-header {
  margin-bottom: 10px;
}
</style>
